import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ResourceRequest,
  ResourceRequestItem,
  useUpdateResourceRequest,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { FormProvider } from 'react-hook-form';
import { AddRequestedResource } from 'components/MapPage/CorporateViewTabs/ResourceTab/AddRequestedResource';
import { CustomResourcesDropdown } from 'components/MapPage/CorporateViewTabs/ResourceTab/CustomResourcesDropdown';
import { useResourceRequestForm } from 'components/IAP/ResourceRequests/hooks/useResourceRequestForm';
import { Ics213RrFormBody } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrFormBody';
import {
  Ics213RrVersioning,
  use213rrVersionLocked,
} from 'components/IAP/ResourceRequests/ics213rr/Ics213RrVersioning';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import {
  useAppendFormSignature,
  useCreateSignedFormVersion,
  useFormVersionTip,
  useRejectFormVersion,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { buildFormCompletion } from 'components/IAP/femaCards/formSignatureCompletion';
import {
  defaultRejectRecipientUserGuid,
  ICS213RR_BASE_ID,
  ICS213RR_REQUESTED_BY_ROLE,
  ICS213RR_RESL_ROLE,
  ICS213RR_SECTION_CHIEF_ROLE,
} from 'components/IAP/ResourceRequests/ics213rr/ics213rrSignatureSlots';
import { routeLabelForSlot } from 'components/IAP/ResourceRequests/ics213rr/ics213rrRoute';
import { Ics213RrRouteDialog } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrRouteDialog';
import { Ics213RrRejectDialog } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrRejectDialog';
import { renderIcs213RrSectionEditor } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrSectionEditors';
import {
  canEditIcs213RrLogisticsOrderCols,
  canEditIcs213RrSection,
  ICS213RR_FINANCE_SIGN_SLOT_ID,
  ICS213RR_LOGISTICS_SIGN_SLOT_ID,
  ICS213RR_RESL_SIGN_SLOT_ID,
  isIcs213RrLogisticsSection,
  type Ics213RrEditingSection,
} from 'components/IAP/ResourceRequests/ics213rr/ics213rrSections';
import Ics204SignDialog from 'components/IAP/planningView/widgets/AssignmentList/Ics204SignDialog';
import { useAppSelector, selectUser } from 'slices/commonSelectors';

type Ics213RrExpandedPanelProps = {
  request: ResourceRequest;
  memberPositions: { positionId: string; positionName: string }[];
  reqCount: Record<string, number>;
  readOnly?: boolean;
};

const isPortaledUiTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    [
      '[data-slot="dialog-content"]',
      '[data-slot="alert-dialog-content"]',
      '[data-slot="select-content"]',
      '[data-slot="dropdown-menu-content"]',
      '[data-slot="popover-content"]',
      '[data-slot="tooltip-content"]',
      '[role="listbox"]',
      '[data-radix-popper-content-wrapper]',
    ].join(', ')
  );
};

const normalizeItems = (items: ResourceRequestItem[]) =>
  items.map((resource) => ({
    ...resource,
    name: resource.description || resource.name,
    description: resource.description || resource.name,
    unit: resource.unit || 'unit',
    status: (resource.status || 'pending') as ResourceRequestItem['status'],
  }));

/** Built outside the component so `Date.now` is not flagged as render impurity. */
const stamp213rrSignature = (
  name: string,
  role: string,
  comments?: string
) => ({
  name,
  role,
  signedAt: Date.now(),
  ...(comments !== undefined ? { comments } : {}),
});

/**
 * Expanded 213RR detail: versioning banner, per-box edit, per-row order edit.
 */
export function Ics213RrExpandedPanel({
  request,
  memberPositions,
  reqCount,
  readOnly = false,
}: Ics213RrExpandedPanelProps) {
  const [routeSlotId, setRouteSlotId] = useState<string | null>(null);
  const [signSlotId, setSignSlotId] = useState<string | null>(null);
  const [rejectSlotId, setRejectSlotId] = useState<string | null>(null);
  const user = useAppSelector(selectUser);
  const { periodNumber } = useCurrentPeriod();
  const { data: tip } = useFormVersionTip(
    ICS213RR_BASE_ID,
    periodNumber,
    request.resource_request_id
  );
  const createSigned = useCreateSignedFormVersion(
    ICS213RR_BASE_ID,
    periodNumber,
    request.resource_request_id
  );
  const appendSignature = useAppendFormSignature(
    ICS213RR_BASE_ID,
    periodNumber,
    request.resource_request_id
  );
  const rejectVersion = useRejectFormVersion(
    ICS213RR_BASE_ID,
    periodNumber,
    request.resource_request_id
  );
  const completion = tip?.signatureSlots?.length
    ? buildFormCompletion(tip.signatureSlots, tip.signatures)
    : null;
  const incompleteSlots =
    !readOnly && completion
      ? completion.slots.filter((slot) => !slot.complete)
      : [];
  const signSlotIds = incompleteSlots.map((slot) => slot.id);
  const routeSlotIds = incompleteSlots
    .map((slot) => slot.id)
    .filter((id) => !!routeLabelForSlot(id));
  /** Reject beside Sign on incomplete boxes except 6. Requested by. */
  const rejectSlotIds = signSlotIds.filter((id) => id !== 'requestedBy');
  const activeSignSlot = completion?.slots.find(
    (slot) => slot.id === signSlotId
  );
  const activeRejectSlot = completion?.slots.find(
    (slot) => slot.id === rejectSlotId
  );
  const reslSlotOpen = incompleteSlots.some(
    (slot) => slot.id === ICS213RR_RESL_SIGN_SLOT_ID
  );
  const logisticsSlotOpen = incompleteSlots.some(
    (slot) => slot.id === ICS213RR_LOGISTICS_SIGN_SLOT_ID
  );
  const financeSlotOpen = incompleteSlots.some(
    (slot) => slot.id === ICS213RR_FINANCE_SIGN_SLOT_ID
  );

  const [editingSection, setEditingSection] =
    useState<Ics213RrEditingSection | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editResourceIndex, setEditResourceIndex] = useState<number | null>(
    null
  );
  const [customResources, setCustomResources] = useState<ResourceRequestItem[]>(
    request?.items || []
  );
  const [logisticsItems, setLogisticsItems] = useState<ResourceRequestItem[][]>(
    request?.logistics_items || []
  );
  /** Attach selections for a new box-4 row before it is saved. */
  const [draftAttach, setDraftAttach] = useState<ResourceRequestItem[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const pendingSectionRef = useRef<Ics213RrEditingSection | null>(null);

  const locked = use213rrVersionLocked(request.resource_request_id);
  const formPreview = useOptionalIcsFormPdfPreview();
  const { mutate: updateResourceRequest } = useUpdateResourceRequest();
  const { form } = useResourceRequestForm({
    requestData: request,
  });

  const isResl = memberPositions.some(
    (pos) => pos.positionName === 'Resource Unit Leader'
  );
  const isLogs = memberPositions.some(
    (pos) => pos.positionName === 'Logistics Section Chief'
  );
  const isFinance = memberPositions.some(
    (pos) => pos.positionName === 'Finance Section Chief'
  );

  const rowsEditable = !readOnly && !locked;
  const canTransfer = completion?.status === 'signed';
  const logisticsColsEditable = canEditIcs213RrLogisticsOrderCols({
    readOnly,
    openSignSlotId: logisticsSlotOpen
      ? ICS213RR_LOGISTICS_SIGN_SLOT_ID
      : null,
  });

  const setDraftAttachAsLogistics: Dispatch<
    SetStateAction<ResourceRequestItem[][]>
  > = (updater) => {
    setDraftAttach((prev) => {
      const as2d = [prev];
      const next = typeof updater === 'function' ? updater(as2d) : updater;
      return next[0] || [];
    });
  };

  useEffect(() => {
    setCustomResources(request.items || []);
    setLogisticsItems(request.logistics_items || []);
  }, [request.items, request.logistics_items]);

  const persistItems = (items: ResourceRequestItem[]) => {
    const normalized = normalizeItems(items);
    setCustomResources(normalized);
    updateResourceRequest({
      requestId: request.resource_request_id,
      data: { items: normalized },
    });
  };

  /** Patch Order # / ETA / Cost (LSC) on one box-4 row while Logistics is signing. */
  const patchItemLogisticsCols = (
    index: number,
    patch: Pick<ResourceRequestItem, 'order_number' | 'eta' | 'cost'>
  ) => {
    setCustomResources((prev) => {
      const next = prev.map((row, i) =>
        i === index ? { ...row, ...patch } : row
      );
      queueMicrotask(() => persistItems(next));
      return next;
    });
  };

  const persistLogisticsItems = (next: ResourceRequestItem[][]) => {
    updateResourceRequest({
      requestId: request.resource_request_id,
      data: { logistics_items: next },
    });
  };

  /** Local item updates; persist when the list shrinks (delete) or a row Save runs. */
  const setItemsLocal: Dispatch<SetStateAction<ResourceRequestItem[]>> = (
    updater
  ) => {
    setCustomResources((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.length < prev.length) {
        queueMicrotask(() => persistItems(next));
      }
      return next;
    });
  };

  /** Attach-resource updates from box 4; always persist logistics_items. */
  const setLogisticsItemsLocal: Dispatch<
    SetStateAction<ResourceRequestItem[][]>
  > = (updater) => {
    setLogisticsItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      queueMicrotask(() => persistLogisticsItems(next));
      return next;
    });
  };

  const canEditSection = (section: Ics213RrEditingSection) => {
    const openSignSlotId = isIcs213RrLogisticsSection(section)
      ? logisticsSlotOpen
        ? ICS213RR_LOGISTICS_SIGN_SLOT_ID
        : null
      : section === 'financeComments'
        ? financeSlotOpen
          ? ICS213RR_FINANCE_SIGN_SLOT_ID
          : null
        : section === 'reslCheckboxes'
          ? reslSlotOpen
            ? ICS213RR_RESL_SIGN_SLOT_ID
            : null
          : null;
    return canEditIcs213RrSection({
      section,
      readOnly,
      tipLocked: locked,
      openSignSlotId,
      isResl,
      isLogs,
      isFinance,
    });
  };

  const submitEditingSection = () => {
    const sectionForm = contentRef.current?.querySelector<HTMLFormElement>(
      '[data-testid^="ics213rr-section-edit-"]'
    );
    sectionForm?.requestSubmit();
  };

  const activateSection = (section: Ics213RrEditingSection) => {
    if (!canEditSection(section)) return;
    if (editingSection === section) return;
    if (editingSection) {
      pendingSectionRef.current = section;
      submitEditingSection();
      return;
    }
    setShowAdd(false);
    setEditResourceIndex(null);
    setDraftAttach([]);
    setEditingSection(section);
  };

  const handleSectionSaved = () => {
    const pending = pendingSectionRef.current;
    pendingSectionRef.current = null;
    setEditingSection(null);
    if (pending) {
      queueMicrotask(() => setEditingSection(pending));
    }
  };

  const handleSectionDiscard = () => {
    pendingSectionRef.current = null;
    setEditingSection(null);
  };

  useEffect(() => {
    if (!editingSection) return;

    const onPointerDown = (event: PointerEvent) => {
      if (isPortaledUiTarget(event.target)) return;
      const editingRoot = contentRef.current?.querySelector(
        '[data-ics204-section-editing="true"]'
      );
      if (!editingRoot) return;
      if (event.target instanceof Node && editingRoot.contains(event.target)) {
        return;
      }
      submitEditingSection();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [editingSection]);

  const profileName =
    user?.profile_settings && 'userName' in user.profile_settings
      ? user.profile_settings.userName.trim()
      : '';
  const defaultSignName = profileName || user?.user_name?.trim() || '';

  const signCollectsComments =
    activeSignSlot?.role === ICS213RR_SECTION_CHIEF_ROLE ||
    activeSignSlot?.role === ICS213RR_RESL_ROLE;

  const confirmActiveSlotSign = (
    name: string,
    _positionTitle?: string,
    comments?: string
  ) => {
    if (!activeSignSlot) return;
    const signature = stamp213rrSignature(
      name,
      activeSignSlot.role,
      signCollectsComments && comments !== undefined ? comments : undefined
    );
    const onSuccess = () => setSignSlotId(null);
    if (activeSignSlot.role === ICS213RR_REQUESTED_BY_ROLE) {
      createSigned.mutate(signature, { onSuccess });
      return;
    }
    appendSignature.mutate(signature, { onSuccess });
  };

  const clearRowEdit = () => {
    setShowAdd(false);
    setEditResourceIndex(null);
    setDraftAttach([]);
  };

  const startAddRow = () => {
    setEditingSection(null);
    setEditResourceIndex(null);
    setDraftAttach([]);
    setShowAdd(true);
  };

  const startEditRow = (index: number) => {
    setEditingSection(null);
    setShowAdd(false);
    setDraftAttach([]);
    setEditResourceIndex(index);
  };

  const renderAttachSlot = (editIndex: number | null) => {
    if (editIndex !== null && editIndex !== undefined) {
      return (
        <CustomResourcesDropdown
          arrIndex={editIndex}
          logisticsItems={logisticsItems[editIndex] || []}
          setLogisticsItems={setLogisticsItemsLocal}
          reqCount={reqCount}
          isDisabled={locked}
          canTransfer={canTransfer}
          requestCreatorUserGuid={request.user_guid}
          requestorPosition={request.requestor_position}
          lockedResourceSetIds={
            customResources[editIndex]?.locked_attach_resource_set_id
              ? [customResources[editIndex].locked_attach_resource_set_id]
              : undefined
          }
        >
          {({ picker }) => picker}
        </CustomResourcesDropdown>
      );
    }
    return (
      <CustomResourcesDropdown
        arrIndex={0}
        logisticsItems={draftAttach}
        setLogisticsItems={setDraftAttachAsLogistics}
        reqCount={reqCount}
        isDisabled={locked}
        canTransfer={canTransfer}
        requestCreatorUserGuid={request.user_guid}
        requestorPosition={request.requestor_position}
      >
        {({ picker }) => picker}
      </CustomResourcesDropdown>
    );
  };

  return (
    <FormProvider {...form}>
      <div
        ref={contentRef}
        className="tw:flex tw:flex-col tw:gap-3 tw:p-3"
        data-testid="ics213rr-expanded-panel"
      >
        <Ics213RrVersioning request={request} readOnly={readOnly} />
        <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
          {formPreview ? (
            <FormExportButton
              formname="ICS_213RR_CG"
              label="Preview ICS-213RR"
              onExport={(format) =>
                void formPreview.exportAsForm({
                  formname: 'ICS_213RR_CG',
                  data: { requestId: request.resource_request_id },
                  mapSketch: null,
                  format,
                })
              }
            />
          ) : null}
        </div>
        <Ics213RrFormBody
          mode="view"
          request={request}
          items={customResources}
          setItems={setItemsLocal}
          logisticsItems={logisticsItems}
          setLogisticsItems={setLogisticsItemsLocal}
          memberPositions={memberPositions}
          reqCount={reqCount}
          locked={locked}
          rowsEditable={rowsEditable}
          logisticsColsEditable={logisticsColsEditable}
          onLogisticsColsPatch={patchItemLogisticsCols}
          canTransfer={canTransfer}
          editingSection={editingSection}
          onSectionActivate={activateSection}
          canEditSection={canEditSection}
          renderSectionEditor={(section) =>
            renderIcs213RrSectionEditor(section, {
              request,
              onDone: handleSectionSaved,
              onCancel: handleSectionDiscard,
            })
          }
          signSlotIds={signSlotIds}
          onSignSlot={(slotId) => setSignSlotId(slotId)}
          signPending={createSigned.isPending || appendSignature.isPending}
          routeSlotIds={routeSlotIds}
          onRouteSlot={(slotId) => setRouteSlotId(slotId)}
          rejectSlotIds={rejectSlotIds}
          onRejectSlot={(slotId) => setRejectSlotId(slotId)}
          rejectPending={rejectVersion.isPending}
          addItemSlot={
            rowsEditable ? (
              <div className="tw:mb-2 tw:flex tw:justify-end">
                <Button
                  type="button"
                  size="sm"
                  data-testid="ics213rr-add-row"
                  onClick={() => {
                    if (showAdd && editResourceIndex === null) {
                      clearRowEdit();
                      return;
                    }
                    startAddRow();
                  }}
                >
                  <Plus className="tw:size-4" />
                  Add Row
                </Button>
              </div>
            ) : null
          }
          addingItem={rowsEditable && showAdd && editResourceIndex === null}
          editingItemIndex={rowsEditable ? editResourceIndex : null}
          renderItemEditor={({ item, editIndex, logisticsCells }) => (
            <AddRequestedResource
              layout="row"
              setCustomResources={(updater) => {
                setCustomResources((prev) => {
                  const next =
                    typeof updater === 'function' ? updater(prev) : updater;
                  queueMicrotask(() => persistItems(next));
                  return next;
                });
              }}
              setLogisticsItems={
                editIndex === null ? setLogisticsItemsLocal : undefined
              }
              draftLogisticsItems={
                editIndex === null ? draftAttach : undefined
              }
              setShowAdd={setShowAdd}
              resourceToEdit={item}
              editIndex={editIndex}
              onClose={clearRowEdit}
              trailingCells={logisticsCells}
              attachSlot={renderAttachSlot(editIndex)}
            />
          )}
          onEditItem={(index) => {
            if (showAdd || editResourceIndex !== null) {
              clearRowEdit();
            }
            startEditRow(index);
          }}
        />
        {routeSlotId ? (
          <Ics213RrRouteDialog
            open
            title={routeLabelForSlot(routeSlotId) ?? ''}
            requestId={request.resource_request_id}
            requestLabel={request.request_number || 'ICS-213RR'}
            onOpenChange={(open) => {
              if (!open) setRouteSlotId(null);
            }}
          />
        ) : null}
        {activeSignSlot ? (
          <Ics204SignDialog
            open
            role={activeSignSlot.role}
            title={`Sign — ${activeSignSlot.label}`}
            description={
              signCollectsComments
                ? 'Type your name to sign this box. Add Comments if needed.'
                : 'Type your name to sign this box.'
            }
            defaultName={defaultSignName}
            collectComments={signCollectsComments}
            defaultComments={
              activeSignSlot.role === ICS213RR_RESL_ROLE
                ? (request.resl_comments ?? '')
                : activeSignSlot.role === ICS213RR_SECTION_CHIEF_ROLE
                  ? (request.section_chief_comments ?? '')
                  : ''
            }
            onClose={() => setSignSlotId(null)}
            onConfirm={confirmActiveSlotSign}
            isPending={createSigned.isPending || appendSignature.isPending}
          />
        ) : null}
        {activeRejectSlot ? (
          <Ics213RrRejectDialog
            open
            requestId={request.resource_request_id}
            requestLabel={request.request_number || 'ICS-213RR'}
            slotLabel={activeRejectSlot.label}
            defaultRequesterUserId={
              defaultRejectRecipientUserGuid(tip?.signatures) ||
              request.user_guid ||
              null
            }
            onOpenChange={(open) => {
              if (!open) setRejectSlotId(null);
            }}
            confirmPending={rejectVersion.isPending}
            onConfirmDraft={async () => {
              if ((tip?.signatures?.length ?? 0) > 0) {
                await rejectVersion.mutateAsync(activeRejectSlot.role);
              }
            }}
          />
        ) : null}
      </div>
    </FormProvider>
  );
}
